/* import __COLOCATED_TEMPLATE__ from './suggestions-banner.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type Model from '@ember-data/model';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import { EntityType } from 'embercom/models/data/entity-types';
import type ArticleContent from 'embercom/models/articles/article-content';
import type ExternalContent from 'embercom/models/external-content';
import type ContentSnippet from 'embercom/models/content-service/content-snippet';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import type IntlService from 'ember-intl/services/intl';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type AppService from 'embercom/services/app-service';

interface Signature {
  Args: {
    activeContent: LocalizedKnowledgeContent & Model;
  };
}

export default class SuggestionsBanner extends Component<Signature> {
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  get pendingContentReviewRequests(): ContentReviewRequest[] {
    if (
      this.args.activeContent.entityType === EntityType.ArticleContent ||
      this.args.activeContent.entityType === EntityType.ContentSnippet ||
      this.args.activeContent.entityType === EntityType.ExternalContent
    ) {
      let activeContent = this.args.activeContent as
        | ArticleContent
        | ContentSnippet
        | ExternalContent;
      return activeContent.contentReviewRequests.filter(
        (request) => request.status === Status.Pending,
      );
    }
    return [];
  }

  get conDupeReviewRequests(): ContentReviewRequest[] {
    return this.pendingContentReviewRequests.filter(
      (request) => request.isContradictingContent || request.isDuplicateContent,
    );
  }

  get hasAnyConDupeReviewRequests(): boolean {
    return this.conDupeReviewRequests.length > 0;
  }

  get bannerLabel(): string {
    if (this.hasAnyConDupeReviewRequests) {
      return this.intl.t(
        this.appService.app.canUseFeature('team-self-serve-ai-insights-addon')
          ? 'knowledge-hub.content-editor.review-suggestion-info-banner.con-dupe-label-recommendations'
          : 'knowledge-hub.content-editor.review-suggestion-info-banner.con-dupe-label',
      );
    }
    return this.intl.t(
      this.appService.app.canUseFeature('team-self-serve-ai-insights-addon')
        ? 'knowledge-hub.content-editor.review-suggestion-info-banner.label-recommendations'
        : 'knowledge-hub.content-editor.review-suggestion-info-banner.label',
    );
  }

  @action
  reviewSuggestions(): void {
    if (this.hasAnyConDupeReviewRequests) {
      // open the first con-dupe review request
      this.knowledgeHubDrawerEditorService.openReviewDrawer({
        activeContentId: this.conDupeReviewRequests[0].entityId,
        activeContentType: objectNames[this.conDupeReviewRequests[0].entityType],
        routeName: 'apps.app.automation.fin-ai-agent.content-suggestions',
      });
    } else {
      this.knowledgeHubDrawerEditorService.openReviewDrawer({
        activeContentId: this.args.activeContent.id,
        activeContentType: objectNames[this.args.activeContent.entityType],
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::SuggestionsBanner': typeof SuggestionsBanner;
    'knowledge-hub/content-editor/suggestions-banner': typeof SuggestionsBanner;
  }
}
